import AddIcon from "@mui/icons-material/AddRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestInfo } from "../../../../api/types/dataCollectionTypes";
import useClientContext from "../../../../context/useClientContext";
import storage from "../../../../storage/storage";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../common/filters/filterState";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import DataRequestsGrid from "./DataRequestsGrid";
import { filterDefinitions, getDataRequestsFilterPayload, selectOptionsResolver } from "./dataRequestsGridFilters";
import { DataRequestsListActionsContextProvider } from "./DataRequestsListActionsContext";
import DeleteDataRequestDialog from "./dialogs/DeleteDataRequestDialog";
import NewDataRequestDialog from "./dialogs/NewDataRequestDialog";
import PublishDataRequestDialog from "./dialogs/PublishDataRequestDialog";

interface DialogState {
  openDialog?: "new_request" | "edit_request" | "publish_request" | "delete_request";
  editedDataRequest?: DataCollectionRequestInfo;
}

const DataRequestsPage = () => {
  usePageTitle("Data Requests");

  const { clientCode, hasPermissions } = useClientContext();

  const filterId = useMemo(() => `${clientCode}_data_collection_requests_filter_v1`, [clientCode]);

  const [dialogState, setDialogState] = useState<DialogState>({});
  const [filterState, setFilterState] = useState(() => getInitialFilterState(filterId, filterDefinitions, []));

  const filterPayload = useMemo(() => getDataRequestsFilterPayload(filterState), [filterState]);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchDataCollectionRequests,
    filterPayload
  );

  const [templates, fetchTemplatesError] = useFetch(adminApi.getDataRequestTemplates);

  const [categories, fetchCategoriesError] = useFetch(adminApi.getObjectAccessCategories);

  const getScenarios = useCallback(() => adminApi.getObjectsAsLookupReferences("PortfolioMonitoringScenario"), []);

  const [scenarios, fetchScenariosError] = useFetch(getScenarios);

  const isFetching = templates === undefined || categories === undefined || scenarios === undefined;
  const fetchError = fetchTemplatesError || fetchCategoriesError || fetchScenariosError;

  if (fetchError) {
    logError(fetchError, "[DataRequestsPage] fetch");
    storage.clearFilterState(`${clientCode}_data_collection_requests_filter_v1`);
    return <DataLoadingFailed title="Could not load data collection requests" />;
  }

  const closeDialogAndRefresh = () => {
    setDialogState({});
    refreshDataSource();
  };

  const handlePublishDataRequest = (dataRequest: DataCollectionRequestInfo) => {
    setDialogState({ openDialog: "publish_request", editedDataRequest: dataRequest });
  };

  const handleDeleteDataRequest = (dataRequest: DataCollectionRequestInfo) => {
    setDialogState({ openDialog: "delete_request", editedDataRequest: dataRequest });
  };

  const hasPermissionsToManage = hasPermissions(["ManagePortfolioMonitoring"]);

  return (
    <FilterContextProvider
      filterState={filterState}
      setFilterState={setFilterState}
      getSelectOptionsForFilter={selectOptionsResolver}
    >
      <DataRequestsListActionsContextProvider
        hasPermissionsToManage={hasPermissionsToManage}
        categories={categories ?? []}
        onPublish={handlePublishDataRequest}
        onDelete={handleDeleteDataRequest}
      >
        <GeneralPageHeader title="Data Requests">
          {hasPermissionsToManage && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogState({ openDialog: "new_request" })}
              disabled={isFetching}
            >
              Add New
            </Button>
          )}
        </GeneralPageHeader>

        <Stack spacing={2} sx={{ width: "100%", pt: 2.5, px: 3, flex: 1 }}>
          <FiltersPanel
            recordCount={totalRows ?? 0}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={refreshDataSource}
          />

          <DataRequestsGrid apiRef={gridApiRef} dataSource={dataSource} filterId={filterId} />
        </Stack>

        {dialogState.openDialog === "new_request" &&
          templates !== undefined &&
          categories !== undefined &&
          scenarios !== undefined && (
            <NewDataRequestDialog
              templates={templates}
              categories={categories}
              scenarios={scenarios}
              onClose={() => setDialogState({})}
              onSaved={closeDialogAndRefresh}
            />
          )}

        {dialogState.editedDataRequest && dialogState.openDialog === "publish_request" && (
          <PublishDataRequestDialog
            dataRequestId={dialogState.editedDataRequest.id}
            dataRequestStatus={dialogState.editedDataRequest.status}
            dataRequestScheduledAt={dialogState.editedDataRequest.scheduledToBePublishedAt}
            onClose={() => setDialogState({})}
            onSaved={closeDialogAndRefresh}
          />
        )}

        {dialogState.editedDataRequest && (
          <DeleteDataRequestDialog
            open={dialogState.openDialog === "delete_request"}
            onClose={() => setDialogState({})}
            onDeleted={closeDialogAndRefresh}
            dataRequestId={dialogState.editedDataRequest.id}
            dataRequestName={dialogState.editedDataRequest.name}
          />
        )}
      </DataRequestsListActionsContextProvider>
    </FilterContextProvider>
  );
};

export default DataRequestsPage;
